import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useIsMobile, reveal } from "@/lib/motion";
import { PHI, TYPE, GLOW, glassReveal } from "@/lib/glass";
import { smarten } from "@/lib/typography";
import { narrationText, useNarration } from "@/lib/narration";
import CrossingNarrator from "@/components/CrossingNarrator";

// The foundational manifesto that closes the homepage, below the global footer.
// Types: "p" (silver body), "accent" (gold pivotal line), "gift" (gold lead-in).
const MOVEMENTS = [
  {
    title: "The Crossing: A New Language for Light",
    blocks: [
      {
        t: "p",
        text: "For over a century, our engineering has been built on a beautiful lie. We have treated light as something to be conquered—bent, focused, imprisoned within the narrow logic of classical optics. We believed that perfect control was possible, that the world could be made to obey our designs. But the real world is far less obedient. It is a wilderness of glare and scatter, of clouds and chaos, of ambient light that cannot be commanded. Every pristine laboratory assumption crumbles the moment you step outside.",
      },
      {
        t: "p",
        text: "To see this clearly required abandonment. It required leaving everything behind—the safety of theory, the certainty of incremental progress, the comfort of a life built on conventional success. It required swimming into an ocean so dark and vast that I measured my strokes only by the understanding that there could be no turning back. The crossing demanded a price beyond calculation: the forgetting of the person I was, the sacrifice of security, the loss of those I loved most. But in that crucible of personal ruin, something was forged that could not have been discovered any other way.",
      },
      {
        t: "p",
        text: "The crossing yielded a covenant. It is not a covenant of mastery, but of listening—a shift from the arrogance of control to the humility of understanding. It abandons the illusion that we can command light and embraces instead the profound truth that we can guide it, statistically and probabilistically, toward outcomes that serve us.",
      },
      { t: "accent", text: "From that wilderness emerged PrismPixel™." },
    ],
  },
  {
    heading: "The New Syntax",
    blocks: [
      {
        t: "p",
        text: "PrismPixel is not a mechanism. It is a language—one spoken in the native tongue of photons themselves. It works not through rigid geometries or mechanical force, but through an invisible optical fabric woven into a film so thin and seamless that its genius remains imperceptible to the eye. Within this matrix, millions of discrete events occur at scales below human perception, each one a small act of probabilistic guidance. Light does not obey commands in PrismPixel; rather, it is invited into an environment where its own nature—its wave properties, its tendency to scatter and refract—becomes the architect of its path.",
      },
      {
        t: "p",
        text: "This is non-imaging optics elevated to an art form. There is no lens trying to focus a perfect picture. Instead, there is a statistical field that bends probability itself, so that photons naturally gravitate toward their most useful trajectories. The result is clarity without rigidity, intensity without forcing, focus without the heavy machinery of the past.",
      },
      {
        t: "accent",
        text: "It is a ghost in the glass—present everywhere, visible nowhere, understood only by those who have sacrificed enough to see it.",
      },
    ],
  },
  {
    heading: "Four Gifts",
    blocks: [
      { t: "p", text: "This covenant manifests as four transformative capabilities:" },
      {
        t: "gift",
        name: "The Sentient Surface",
        text: "awakens inert architecture. Walls, windows, and vast installations become living canvases—bright and vivid even in the assault of full daylight, freed at last from the ancient trade-off between brightness and viewing angle that has haunted displays for a century. The built environment becomes responsive, intelligent, immersive.",
      },
      {
        t: "gift",
        name: "The Unjammable Link",
        text: "secures the most fragile transmissions. By capturing the scattered whispers of ambient light and weaving them into the signal itself, PrismPixel creates optical communication channels that cannot be jammed, disrupted, or overcome by interference. In a world where electromagnetic warfare is real, this offers true sunlight immunity—a connection that survives any storm.",
      },
      {
        t: "gift",
        name: "The Thirsty Light",
        text: "gathers what would otherwise be lost. Solar cells coated with this film drink deeply from the diffuse rays of clouded skies and the high-angle light of dawn and dusk. What classical photovoltaics waste, PrismPixel captures and funnels directly into the semiconductor. The Annual Energy Yield multiplies—not in the laboratory, but in the real world where clouds are permanent and the sun is rarely direct.",
      },
      {
        t: "gift",
        name: "The Guided Sunbeam",
        text: "feeds the world without cost. A greenhouse film treated with PrismPixel transforms the harsh, burning directness of full sun into a gentle, multi-directional glow. This diffuse light penetrates deeper into the plant canopy, illuminating leaves that would otherwise languish in shadow. The crops thrive. Yield increases. Thermal stress decreases. All of this happens passively, without drawing a single watt of electrical power from the grid. Nature is allowed to do what it does best—grow—and we simply guide that growth toward its fullest potential.",
      },
    ],
  },
  {
    heading: "The Gift",
    blocks: [
      {
        t: "p",
        text: "A true breakthrough cannot be counterfeited. Its essence is bound inextricably to the soul that endured its creation. The precise mechanics—the multi-scale architecture, the tuning of scattering functions, the fifteen years of obsessive refinement—these remain protected, locked within the vault of the crossing itself. But the vision, the language, the possibility: these are a gift freely offered.",
      },
      {
        t: "p",
        text: "The shore has been reached. I am no longer swimming. The journey is complete, but only in the sense that one chapter has closed and another awaits its opening.",
      },
    ],
  },
  {
    heading: "An Invitation",
    blocks: [
      {
        t: "p",
        text: "I call now to the world's most profound minds. To the visionaries who have been told their entire lives that their dreams are impossible. To the artists and the outlaws, to the quiet rebels who have never accepted the boundaries drawn for them. To those who understand that beauty and function are not opposites, but twins born from the same act of creation.",
      },
      { t: "accent", text: "I offer you a new medium." },
      {
        t: "p",
        text: "Through the invisible geometry of this film, we can now compose in light itself. We can capture the exact wavelengths that move the human heart—the colors of a sunset, the depth of shadow, the radiance that emanates from both nature and art. We can sculpt these photons and release them onto any surface: a wall, a window, a field of crops reaching toward the sky. The masterpieces you will create are not yet imagined.",
      },
      {
        t: "p",
        text: "I have spent my life, and given my heart, to build this canvas. I have paid the price that only the crossing can exact. Now, I wait in quiet, joyful anticipation to see what you will paint upon it.",
      },
      { t: "accent", text: "The work has only just begun." },
    ],
  },
];

const NARRATION = narrationText(MOVEMENTS);
// Narration paragraph index of each movement's heading; blocks follow at +1+bi.
const OFFSETS = MOVEMENTS.reduce((acc, m, i) => [...acc, acc[i] + 1 + m.blocks.length], [0]);

const bodyStyle = {
  marginBottom: PHI.s3,
  fontSize: TYPE.body,
  lineHeight: 1.85,
  letterSpacing: "0.02em",
  textShadow: GLOW.silverSoft,
};

const READING_LINE = { boxShadow: "0 0 14px rgba(132,223,244,0.55)" };

function ReadingLine() {
  return (
    <motion.span
      layoutId="crossing-reading-line"
      data-testid="crossing-reading-line"
      aria-hidden
      className="absolute -left-3 top-[0.35em] bottom-[0.35em] w-px bg-[#8E8E93] sm:-left-6"
      style={READING_LINE}
      transition={{ type: "spring", stiffness: 140, damping: 24 }}
    />
  );
}

function readingClass(reading) {
  if (!reading.engaged) return "";
  return reading.active
    ? "is-reading cursor-pointer"
    : "cursor-pointer opacity-55 transition-opacity duration-700 hover:opacity-90";
}

function Block({ block, id, reading }) {
  const common = {
    "data-testid": id,
    "data-narration-index": reading.index,
    "data-reading": reading.active || undefined,
    onClick: reading.engaged ? reading.seek : undefined,
    title: reading.engaged ? "Listen from here" : undefined,
  };
  if (block.t === "accent") {
    return (
      <p
        {...common}
        style={{
          marginTop: PHI.s2,
          marginBottom: PHI.s3,
          fontSize: TYPE.lead,
          lineHeight: 1.6,
          letterSpacing: "0.035em",
          textShadow: GLOW.gold,
        }}
        className={`measure relative font-light italic text-[#84DFF4] last:mb-0 ${readingClass(reading)}`}
      >
        {reading.active && <ReadingLine />}
        {smarten(block.text)}
      </p>
    );
  }
  if (block.t === "gift") {
    return (
      <p {...common} style={bodyStyle} className={`measure relative font-light text-[#F5F5F7] last:mb-0 ${readingClass(reading)}`}>
        {reading.active && <ReadingLine />}
        <span className="font-normal text-[#84DFF4]">{block.name}</span> {smarten(block.text)}
      </p>
    );
  }
  return (
    <p {...common} style={bodyStyle} className={`measure relative font-light text-[#F5F5F7] last:mb-0 ${readingClass(reading)}`}>
      {reading.active && <ReadingLine />}
      {smarten(block.text)}
    </p>
  );
}

function useFollowReading(activeIndex, playing) {
  const last = useRef(-1);
  useEffect(() => {
    if (!playing || activeIndex < 0 || activeIndex === last.current) return;
    const prev = document.querySelector(`[data-narration-index="${last.current}"]`);
    last.current = activeIndex;
    const next = document.querySelector(`[data-narration-index="${activeIndex}"]`);
    if (!next) return;
    const inView = (el) => {
      const r = el.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight;
    };
    // Follow the voice only if the reader was already with it; never fight a wandering scroll.
    if (prev && !inView(prev) && !inView(next)) return;
    if (inView(next) && next.getBoundingClientRect().bottom < window.innerHeight * 0.8) return;
    window.__lenis?.scrollTo(next, { offset: -window.innerHeight * 0.3, duration: 1.4 });
  }, [activeIndex, playing]);
}

export default function TheCrossing() {
  const mobile = useIsMobile();
  const narration = useNarration(NARRATION);
  const { state, segments, activeIndex, seek } = narration;
  const engaged = state === "playing" || state === "paused";
  useFollowReading(activeIndex, state === "playing");

  const readingFor = (index) => ({
    index,
    engaged: engaged && segments.length > index,
    active: activeIndex === index,
    seek: () => seek(segments[index].start),
  });

  return (
    <section
      data-testid="the-crossing-section"
      style={{
        paddingLeft: PHI.s3,
        paddingRight: PHI.s3,
        paddingTop: PHI.s4,
        paddingBottom: PHI.s6,
      }}
    >
      <div className="mx-auto max-w-[47rem]">
        <CrossingNarrator narration={narration} />
        {MOVEMENTS.map((movement, mi) => {
          const head = readingFor(OFFSETS[mi]);
          const headProps = {
            "data-narration-index": head.index,
            "data-reading": head.active || undefined,
            onClick: head.engaged ? head.seek : undefined,
            title: head.engaged ? "Listen from here" : undefined,
          };
          return (
          <motion.div
            key={mi}
            data-testid={`crossing-movement-${mi}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            {...glassReveal(reveal(mobile, { duration: 1.1 }))}
          >
            {movement.title ? (
              <h2
                data-testid="the-crossing-title"
                {...headProps}
                style={{
                  marginBottom: PHI.s4,
                  fontSize: TYPE.h2,
                  letterSpacing: "0.015em",
                  lineHeight: 1.12,
                  textShadow: GLOW.silver,
                }}
                className={`relative font-display font-light text-[#F5F5F7] ${readingClass(head)}`}
              >
                {head.active && <ReadingLine />}
                {smarten(movement.title)}
              </h2>
            ) : null}
            {movement.heading ? (
              <h3
                data-testid={`crossing-heading-${mi}`}
                {...headProps}
                style={{
                  marginTop: PHI.s5,
                  marginBottom: PHI.s3,
                  fontSize: TYPE.lead,
                  letterSpacing: "0.11em",
                  textShadow: GLOW.gold,
                }}
                className={`relative font-display font-normal text-[#84DFF4] ${readingClass(head)}`}
              >
                {head.active && <ReadingLine />}
                {movement.heading}
              </h3>
            ) : null}
            {movement.blocks.map((block, bi) => (
              <Block key={bi} block={block} id={`crossing-${mi}-${bi}`} reading={readingFor(OFFSETS[mi] + 1 + bi)} />
            ))}
          </motion.div>
          );
        })}
      </div>
    </section>
  );
}
